import { createSelector } from "@reduxjs/toolkit"
import { Configuration } from "shared/configuration"
import { ConfigurationThingTypeMrn } from "shared/configurationData"
import { SharedAccessPatternOsEnum } from "shared/sharedAccessPatternsData"
import { useAppSelector } from "src/app/hooks"
import type { RootState } from "src/app/store"
import { selectMeIsReady, selectTenantId } from "src/features/me/meSlice"
import { useGetThingsQuery } from "src/features/things/thingsApi"
import { selectAllThings } from "src/features/things/thingsSlice"
import { DEFAULT_EXPIRATION_WARNING_MINUTES } from "../sst2DoorState"

const CONFIG_REF_NAME = "configAboutToExpirationReminderDelayMinutes"

/**
 * Memoized so the ~30 door tiles on a locker dashboard share one lookup per
 * store change instead of each scanning the things collection. Returns a
 * number, so a cache miss still can't cause re-render churn.
 */
const selectExpirationWarningMinutes = createSelector(
  [
    (state: RootState) => selectAllThings(state),
    (_state: RootState, tenantId?: string) => tenantId,
  ],
  (things, tenantId) => {
    const config = things.find(
      (thing) =>
        thing.thingTypeMrn === ConfigurationThingTypeMrn &&
        thing.tenantId === tenantId &&
        (thing as unknown as Configuration).refName === CONFIG_REF_NAME,
    ) as unknown as Configuration | undefined

    return config?.nVal || DEFAULT_EXPIRATION_WARNING_MINUTES
  },
)

/**
 * The tenant's "about to expire" window, in minutes — the threshold at which a
 * locker door tile turns orange before its reservation expires.
 *
 * V1 reads this config inside `useReservation`; V2 door tiles hard-coded 15
 * minutes, so on a V2 locker the warning never appeared for the configured
 * window (default 1440 = 24h). This hook is the V2 equivalent.
 *
 * Safe to call from every door tile: RTK Query dedupes the identical query
 * args into a single request, and the hook returns a primitive so extra store
 * subscriptions can't trigger re-render churn.
 */
export function useExpirationWarningMinutes(): number {
  const tenantId = useAppSelector(selectTenantId)
  const isReady = useAppSelector(selectMeIsReady)

  useGetThingsQuery(
    {
      params: {
        "ap.name": SharedAccessPatternOsEnum.GetConfigurationsByTenant,
        "ap.tenantId": tenantId,
        "ap.refNames[0]": CONFIG_REF_NAME,
        tenantId,
      },
    },
    { skip: !tenantId || !isReady },
  )

  return useAppSelector((state) =>
    selectExpirationWarningMinutes(state, tenantId),
  )
}
